import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, KeyRound, Hash } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { InputGroup } from "@/components/ui/Input";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// VendorLogin
//
// Screen — /vendor-portal/login
// Two-step: enter token → enter PIN (OTP, 6 digits)
// No sidebar (ExternalLayout wrapper in App.tsx)
// Design: §8.5 Vendor Portal Login
// ─────────────────────────────────────────────

type Step = "token" | "pin";

// 6-digit PIN input rendered as individual cells
function PinInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const digits = value.split("").slice(0, 6);
  while (digits.length < 6) digits.push("");

  function handleChange(idx: number, char: string) {
    const next = [...digits];
    next[idx] = char.replace(/\D/g, "").slice(-1);
    const joined = next.join("");
    onChange(joined);

    // Auto-focus next
    if (char && idx < 5) {
      const nextInput = document.getElementById(`pin-${idx + 1}`);
      nextInput?.focus();
    }
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      const prevInput = document.getElementById(`pin-${idx - 1}`);
      prevInput?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    document.getElementById(`pin-${focusIdx}`)?.focus();
  }

  return (
    <div className="flex gap-2">
      {digits.map((d, idx) => (
        <input
          key={idx}
          id={`pin-${idx}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={idx === 0 ? handlePaste : undefined}
          className={cn(
            "h-12 w-12 rounded-control border bg-bg-input text-center",
            "text-[20px] font-mono font-semibold text-text-primary",
            "transition-colors focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent",
            d ? "border-accent" : "border-border-default"
          )}
        />
      ))}
    </div>
  );
}

export function VendorLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("token");
  const [token, setToken] = useState("");
  const [pin, setPin] = useState("");

  const verifyTokenMutation = useMutation({
    mutationFn: async (tokenValue: string) => {
      const res = await fetch("/api/v1/vendor-portal/verify-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenValue }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Known code → localized message; the API message itself is English.
        if (data?.error?.code === "ASSESSMENT_NOT_ACCESSIBLE") {
          throw new Error(t("vendorPortal.assessmentNotAccessible"));
        }
        throw new Error(data?.error?.message ?? t("errors.invalidToken"));
      }
      return res.json();
    },
    onSuccess: () => {
      setStep("pin");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const verifyPinMutation = useMutation({
    mutationFn: async ({ t: tokenValue, p }: { t: string; p: string }) => {
      const res = await fetch("/api/v1/vendor-portal/verify-pin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenValue, pin: p }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message ?? t("errors.invalidPin"));
      }
      return res.json() as Promise<{ data: { ok: boolean; sessionToken: string } }>;
    },
    onSuccess: (result) => {
      // Store session token in localStorage keyed by access token
      const sessionToken = result?.data?.sessionToken;
      if (sessionToken) {
        localStorage.setItem(`vendor_session_${token}`, sessionToken);
      }
      navigate(`/vendor-portal/${token}`);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setPin("");
    },
  });

  function handleTokenSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token.trim()) {
      toast.error(t("vendorPortal.tokenRequired"));
      return;
    }
    verifyTokenMutation.mutate(token.trim());
  }

  function handlePinSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pin.length !== 6) {
      toast.error(t("vendorPortal.pinIncomplete"));
      return;
    }
    verifyPinMutation.mutate({ t: token, p: pin });
  }

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div
        className="w-full max-w-[400px] rounded-card border border-border-default bg-card p-8"
      >
        {/* Logo */}
        <div className="mb-6 text-center">
          <span className="block font-mono text-[20px] font-medium tracking-[3px] text-text-primary">
            KIBORG
          </span>
          <p className="mt-1 text-caption text-text-muted">{t("vendorPortal.title")}</p>
        </div>

        {step === "token" ? (
          <>
            <div className="mb-6 flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-bg">
                <KeyRound className="h-6 w-6 text-accent" />
              </div>
              <h1 className="text-[18px] font-semibold text-text-primary">{t("vendorPortal.enterToken")}</h1>
              <p className="text-center text-body text-text-muted">
                {t("vendorPortal.tokenDesc")}
              </p>
            </div>

            <form onSubmit={handleTokenSubmit} className="flex flex-col gap-4">
              <InputGroup
                label={t("vendorPortal.accessToken")}
                placeholder={t("vendorPortal.tokenPlaceholder")}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
                autoFocus
              />
              <Button type="submit" className="w-full" isLoading={verifyTokenMutation.isPending}>
                {t("vendorPortal.continue")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="mb-6 flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-bg">
                <Hash className="h-6 w-6 text-accent" />
              </div>
              <h1 className="text-[18px] font-semibold text-text-primary">{t("vendorPortal.enterPin")}</h1>
              <p className="text-center text-body text-text-muted">
                {t("vendorPortal.pinDesc")}
              </p>
            </div>

            <form onSubmit={handlePinSubmit} className="flex flex-col gap-6">
              <div className="flex justify-center">
                <PinInput value={pin} onChange={setPin} />
              </div>
              <Button
                type="submit"
                className="w-full"
                isLoading={verifyPinMutation.isPending}
                disabled={pin.length !== 6}
              >
                {t("vendorPortal.accessAssessment")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button
                type="button"
                onClick={() => { setStep("token"); setPin(""); }}
                className="text-center text-caption text-text-muted hover:text-text-primary"
              >
                {t("vendorPortal.backToToken")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
